import { ApolloProvider } from '@apollo/client';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useEffect, useState } from 'react';
import { Navigate, Outlet, RouterProvider, useLocation } from 'react-router-dom';

import { Layout } from './components/layout';
import { ParkingLoader } from './components/ui/parking-loader';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import { ConfirmationProvider } from './components/ui/confirmation-dialog';
import './i18n'; // Initialize i18n

import { apolloClient } from '@/lib/apolloClient';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { AlertSubscriptionProvider } from '@/contexts/AlertSubscriptionContext';
import { EntityModalProvider } from '@/contexts/EntityModalContext';
import { Login } from '@/pages/auth/login';
import { DetachedEntityView } from '@/pages/detached/detached-entity-view';
import { sentryCreateBrowserRouter } from './sentry';

ModuleRegistry.registerModules([AllCommunityModule]);

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { isAuthenticated, isLoading } = useAuth();
	const location = useLocation();

	// Overlay: show for 1.5s then fade out
	const [visible, setVisible] = useState(true);
	const [fadingOut, setFadingOut] = useState(false);

	useEffect(() => {
		// After 400ms, start fading out the loader
		const timer = setTimeout(() => {
			setFadingOut(true);
			// After fade transition completes, unmount overlay
			setTimeout(() => {
				setVisible(false);
				setFadingOut(false);
			}, 1400);
		}, 400);
		return () => clearTimeout(timer);
	}, []);

	// Only redirect after auth has resolved
	if (!isAuthenticated && !isLoading) {
		return <Navigate to="/login" replace state={{ from: location }} />;
	}

	return (
		<>
			{/* Always render content immediately so the screen loads behind the overlay */}
			{isAuthenticated && children}

			{/* Loader overlay: visible for 1.5s, then fades out over rendered content */}
			{visible && (
				<div
					className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-4 bg-background/50 text-muted-foreground"
					style={{
						opacity: fadingOut ? 0 : 1,
						backdropFilter: fadingOut ? 'blur(0px)' : 'blur(4px)',
						WebkitBackdropFilter: fadingOut ? 'blur(0px)' : 'blur(4px)',
						pointerEvents: fadingOut ? 'none' : 'auto',
						transition: 'opacity 1.4s ease-out, backdrop-filter 1.4s ease-out, -webkit-backdrop-filter 1.4s ease-out',
					}}
				>
					<ParkingLoader size={120} label="Loading..." />
				</div>
			)}
		</>
	);
};

const ProtectedRoutes = () => (
	<RequireAuth>
		<Layout />
	</RequireAuth>
);

// Root layout that wraps all routes with EntityModalProvider (inside Router context)
const RootLayout = () => (
	<EntityModalProvider>
		<Outlet />
		<Toaster />
	</EntityModalProvider>
);

const router = sentryCreateBrowserRouter([
	{
		element: <RootLayout />,
		children: [
			{
				path: '/login',
				element: <Login />
			},
			{
				path: '/view/:entityType/:entityId',
				element: (
					<RequireAuth>
						<DetachedEntityView />
					</RequireAuth>
				)
			},
			{
				path: '/*',
				element: <ProtectedRoutes />
			}
		]
	}
]);

function App() {
	return (
		<ApolloProvider client={apolloClient}>
			<AuthProvider>
				<AlertSubscriptionProvider>
					<ThemeProvider>
						<ConfirmationProvider>
							<RouterProvider router={router} />
						</ConfirmationProvider>
					</ThemeProvider>
				</AlertSubscriptionProvider>
			</AuthProvider>
		</ApolloProvider>
	);
}

export default App;
